import { MenuItem, TextField, Tooltip } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useOutlets } from '@/api/hooks'
import { useAuthStore } from '@/auth/authStore'

/**
 * Which branch is being worked in, narrowing every screen to it.
 *
 * <p>Beside the company for the same reason: somebody who works in two of them
 * is reading the same screens for both, and the figures mean nothing until you
 * know whose they are. A manager standing in one branch sets it once and stops
 * seeing another branch's orders, kitchen list and takings.</p>
 *
 * <p>Only appears for somebody who may work in more than one. Everyone else has
 * one branch and never has to think about it - the server already answers for
 * the outlets they may reach, so nothing is being hidden from them here.</p>
 *
 * <p>"All branches" is the whole business, which is what somebody who oversees
 * several of them wants most of the time.</p>
 */
export function OutletSwitcher() {
  const queryClient = useQueryClient()
  const activeOutletId = useAuthStore((state) => state.activeOutletId)
  const setActiveOutlet = useAuthStore((state) => state.setActiveOutlet)
  const { data: outlets } = useOutlets()

  const mine = outlets ?? []

  // A branch remembered from the company just left, or one somebody's access
  // was withdrawn from, would filter every screen down to nothing with no way
  // of telling why. Falls back to all of them.
  useEffect(() => {
    if (activeOutletId && mine.length > 0 && !mine.some((outlet) => outlet.id === activeOutletId)) {
      setActiveOutlet(null)
      void queryClient.resetQueries()
    }
  }, [activeOutletId, mine, setActiveOutlet, queryClient])

  if (mine.length < 2) {
    return null
  }

  return (
    <Tooltip title="The branch you are working in">
      <TextField
        select
        size="small"
        value={activeOutletId ?? ''}
        onChange={(event) => {
          setActiveOutlet(event.target.value || null)
          // Everything loaded was fetched for the branch being left. Same
          // reasoning as the company switcher: drop it and refetch what is on
          // screen, rather than leave stale figures under a new heading.
          void queryClient.resetQueries()
        }}
        sx={{ minWidth: 170, mr: 1 }}
        inputProps={{ 'aria-label': 'Branch' }}
      >
        <MenuItem value="">All branches</MenuItem>
        {mine.map((outlet) => (
          <MenuItem key={outlet.id} value={outlet.id}>
            {outlet.code} — {outlet.name}
          </MenuItem>
        ))}
      </TextField>
    </Tooltip>
  )
}
